import { useMsal } from '@azure/msal-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getMicrosoftRedirectUri, marketplaceLoginRequest } from '../../auth/authConfig';
import { useAppSelector } from '../../stores';
import MicrosoftService, {
    MarketplaceApiError,
    MicrosoftActivationResponse,
    getMarketplaceErrorCode,
    resolveMarketplaceErrorMessage,
} from '../../services/api/microsoftService';
import { Images } from '../../constants';
import Routes from '../../routes/Routes';
import {
    clearMarketplaceFlow,
    getMarketplaceToken,
    isMarketplaceLoginPending,
    setMarketplaceLoginPending,
    clearMarketplaceLoginPending,
    shouldResumeMarketplaceFlow,
    startMarketplaceFlow,
} from '../../utils/marketplaceSession';
import './MicrosoftPurchaseLanding.css';

type ActivationStatus =
    | 'awaiting_sso'
    | 'sso_redirecting'
    | 'connecting_famla'
    | 'creating_workspace'
    | 'processing'
    | 'success_paid'
    | 'success_free'
    | 'error'
    | 'tenant_mismatch';

const ACTIVATION_MAX_ATTEMPTS = 4;
const ACTIVATION_RETRY_BASE_MS = 2500;

const sleep = (ms: number) => new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
});

const isRetryableActivationError = (error: MarketplaceApiError): boolean => {
    const errorCode = getMarketplaceErrorCode(error?.detail);
    return errorCode === 'ACTIVATION_RETRY' || error.status === 503 || error.status === 500;
};

const MicrosoftLogoIcon: React.FC = () => (
    <svg className="ms-purchase__brand-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#F35325" d="M11.4 2v9.4H2V2h9.4z" />
        <path fill="#81BC06" d="M22 2v9.4h-9.4V2H22z" />
        <path fill="#05A6F0" d="M11.4 22v-9.4H2V22h9.4z" />
        <path fill="#FFBA08" d="M22 22v-9.4h-9.4V22H22z" />
    </svg>
);

const MicrosoftButtonIcon: React.FC = () => (
    <svg className="ms-purchase__btn-icon" viewBox="0 0 21 21" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
);

const MicrosoftPurchaseLanding: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { instance, accounts } = useMsal();
    const auth = useAppSelector((state) => state.auth);

    const [status, setStatus] = useState<ActivationStatus>('processing');
    const [message, setMessage] = useState('');
    const [creditsAdded, setCreditsAdded] = useState(0);
    const [marketplaceToken, setMarketplaceToken] = useState<string | null>(null);
    const activationStarted = useRef(false);
    const ssoAutoTriggered = useRef(false);

    const resolveToken = useCallback(() => {
        const urlToken = searchParams.get('token');
        if (urlToken) {
            startMarketplaceFlow(urlToken);
            return urlToken;
        }
        return shouldResumeMarketplaceFlow() ? getMarketplaceToken() : null;
    }, [searchParams]);

    const activateMicrosoftSubscription = useCallback(async (token: string) => {
        if (!auth.access_token) {
            return;
        }

        try {
            setStatus('processing');
            setMessage(t('microsoft.activating'));

            let data: MicrosoftActivationResponse | undefined;
            let lastError: MarketplaceApiError | null = null;

            for (let attempt = 0; attempt < ACTIVATION_MAX_ATTEMPTS; attempt += 1) {
                try {
                    data = await MicrosoftService.activateSubscription(token, auth.access_token);
                    lastError = null;
                    break;
                } catch (attemptError: unknown) {
                    const apiError = attemptError as MarketplaceApiError;
                    lastError = apiError;
                    const canRetry =
                        attempt < ACTIVATION_MAX_ATTEMPTS - 1
                        && isRetryableActivationError(apiError);

                    if (!canRetry) {
                        throw attemptError;
                    }

                    setMessage(t('microsoft.activating_retry', {
                        attempt: attempt + 2,
                        max: ACTIVATION_MAX_ATTEMPTS,
                    }));
                    await sleep(ACTIVATION_RETRY_BASE_MS * (attempt + 1));
                }
            }

            if (!data) {
                throw lastError ?? new Error('Activation failed');
            }

            const activation = data;

            if (activation.status === 'already_processed') {
                clearMarketplaceFlow();
                navigate(Routes.HOMEVIEW, { replace: true });
                return;
            }

            clearMarketplaceFlow();

            if (activation.plan_id === 'free_starter') {
                setStatus('success_free');
                setMessage(t('microsoft.free_starter_upgrade_message'));
                setTimeout(() => {
                    navigate(Routes.DASHBOARDS);
                }, 2500);
                return;
            }

            setStatus('success_paid');
            setCreditsAdded(activation.credits_added ?? 0);
            setMessage(t('microsoft.success', { credits: activation.credits_added ?? 0 }));
            setTimeout(() => {
                navigate(`/home?purchase=success&source=microsoft&credits=${activation.credits_added ?? 0}`);
            }, 2500);
        } catch (error: unknown) {
            console.error('[Microsoft] Activation error:', error);
            const apiError = error as MarketplaceApiError;
            const detail = apiError?.detail;
            const errorCode = getMarketplaceErrorCode(detail);
            const errorMessage = resolveMarketplaceErrorMessage(t, detail);

            if (errorCode === 'TENANT_MISMATCH') {
                setStatus('tenant_mismatch');
                setMessage(errorMessage);
                return;
            }

            if (errorCode === 'REQUIRES_WORKSPACE') {
                setStatus('creating_workspace');
                setMessage(errorMessage);
                return;
            }

            setStatus('error');
            setMessage(errorMessage);
            toast.error(errorMessage);
        }
    }, [auth.access_token, navigate, t]);

    useEffect(() => {
        const urlToken = searchParams.get('token');
        if (!urlToken && getMarketplaceToken() && !shouldResumeMarketplaceFlow()) {
            clearMarketplaceFlow();
        }

        if (
            auth.access_token
            && auth.session?.company_id
            && !urlToken
            && !shouldResumeMarketplaceFlow()
        ) {
            clearMarketplaceFlow();
            navigate(Routes.HOMEVIEW, { replace: true });
            return;
        }

        const token = resolveToken();
        setMarketplaceToken(token);

        if (!token) {
            setStatus('error');
            setMessage(t('microsoft.error.no_token'));
            return;
        }

        if (!auth.access_token) {
            if (accounts.length > 0) {
                setStatus('connecting_famla');
                setMessage(t('microsoft.connecting_famla'));
                if (!isMarketplaceLoginPending()) {
                    setMarketplaceLoginPending();
                    navigate(Routes.AUTH_LOGIN, { replace: true });
                }
            } else {
                clearMarketplaceLoginPending();
                setStatus('sso_redirecting');
                setMessage(t('microsoft.connect_microsoft'));
            }
            activationStarted.current = false;
            return;
        }

        if (!auth.session?.company_id) {
            setStatus('creating_workspace');
            setMessage(t('microsoft.workspace_required'));
            activationStarted.current = false;
            return;
        }

        if (activationStarted.current) {
            return;
        }
        activationStarted.current = true;
        activateMicrosoftSubscription(token);
    }, [
        searchParams,
        auth.access_token,
        auth.session?.company_id,
        accounts.length,
        navigate,
        resolveToken,
        activateMicrosoftSubscription,
        t,
    ]);

    const handleRetry = () => {
        if (!marketplaceToken || !auth.access_token) {
            return;
        }
        activationStarted.current = true;
        activateMicrosoftSubscription(marketplaceToken);
    };

    const handleMicrosoftSso = useCallback(async () => {
        const token = marketplaceToken || resolveToken();
        if (!token) {
            return;
        }

        startMarketplaceFlow(token);
        sessionStorage.removeItem('microsoft_manual_logout');
        sessionStorage.setItem('microsoft_auth_intent', 'login');

        if (accounts.length > 0) {
            setMarketplaceLoginPending();
            setStatus('connecting_famla');
            setMessage(t('microsoft.connecting_famla'));
            navigate(Routes.AUTH_LOGIN, { replace: true });
            return;
        }

        clearMarketplaceLoginPending();

        setStatus('sso_redirecting');
        setMessage(t('microsoft.connect_microsoft'));

        try {
            await instance.loginRedirect({
                ...marketplaceLoginRequest,
                redirectUri: getMicrosoftRedirectUri('/auth/login'),
            });
        } catch (error) {
            console.error('[Microsoft Marketplace] SSO redirect failed:', error);
            setStatus('awaiting_sso');
            setMessage(t('microsoft.free_starter_awaiting_sso'));
            toast.error(t('views.auth.anErrorOcured'));
        }
    }, [accounts.length, instance, marketplaceToken, navigate, resolveToken, t]);

    useEffect(() => {
        if (!marketplaceToken || auth.access_token) {
            return;
        }
        if (accounts.length > 0) {
            return;
        }
        if (ssoAutoTriggered.current) {
            return;
        }
        if (isMarketplaceLoginPending()) {
            return;
        }

        ssoAutoTriggered.current = true;
        void handleMicrosoftSso();
    }, [marketplaceToken, auth.access_token, accounts.length, handleMicrosoftSso]);

    const isSuccess = status === 'success_paid' || status === 'success_free';

    return (
        <div className="ms-purchase">
            <aside className="ms-purchase__left">
                <div className="ms-purchase__left-inner animate-fade-in">
                    <img
                        src={Images.logov1}
                        alt={t('microsoft.logo_alt')}
                        className="ms-purchase__logo"
                    />
                    <p className="ms-purchase__tagline">
                        {t('views.auth.establish')}
                    </p>
                </div>
            </aside>

            <main className="ms-purchase__right">
                <img
                    src={Images.logov1}
                    alt={t('microsoft.logo_alt')}
                    className="ms-purchase__mobile-logo"
                />

                <div className="ms-purchase__content animate-fade-in-up">
                    <div className="ms-purchase__brands">
                        <MicrosoftLogoIcon />
                        <span className="ms-purchase__brand-sep">{t('microsoft.integration_label')}</span>
                        <img
                            src={Images.logov1}
                            alt={t('microsoft.logo_alt')}
                            className="ms-purchase__brand-logo"
                        />
                    </div>

                    <div className="ms-purchase__card">
                        <div className="ms-purchase__card-header">
                            <h1 className="ms-purchase__card-title">{t('microsoft.marketplace_title')}</h1>
                            <p className="ms-purchase__card-subtitle">{t('microsoft.configuring')}</p>
                        </div>

                        <div className="ms-purchase__card-body">
                            {status === 'processing' && (
                                <>
                                    <div className="ms-purchase__spinner" role="status" />
                                    <p className="ms-purchase__message">{message}</p>
                                </>
                            )}

                            {status === 'connecting_famla' && (
                                <>
                                    <div className="ms-purchase__spinner" role="status" />
                                    <h2 className="ms-purchase__heading">{t('microsoft.sso_required_title')}</h2>
                                    <p className="ms-purchase__message">{message}</p>
                                </>
                            )}

                            {status === 'creating_workspace' && (
                                <>
                                    <h2 className="ms-purchase__heading">{t('microsoft.sso_required_title')}</h2>
                                    <p className="ms-purchase__message">{message}</p>
                                    <div className="ms-purchase__actions">
                                        <button
                                            type="button"
                                            onClick={() => navigate(Routes.CREATE_MY_COMPANY)}
                                            className="ms-purchase__btn ms-purchase__btn--primary"
                                        >
                                            {t('microsoft.connect_microsoft')}
                                        </button>
                                        {marketplaceToken && auth.access_token && (
                                            <button
                                                type="button"
                                                onClick={handleRetry}
                                                className="ms-purchase__link"
                                            >
                                                {t('microsoft.retry_activation')}
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}

                            {(status === 'awaiting_sso' || status === 'sso_redirecting') && (
                                <>
                                    <h2 className="ms-purchase__heading">{t('microsoft.sso_required_title')}</h2>
                                    <p className="ms-purchase__message">{message}</p>
                                    <div className="ms-purchase__spinner" role="status" />
                                    {status === 'awaiting_sso' && (
                                        <div className="ms-purchase__actions">
                                            <button
                                                type="button"
                                                onClick={() => void handleMicrosoftSso()}
                                                className="ms-purchase__btn ms-purchase__btn--primary"
                                            >
                                                <MicrosoftButtonIcon />
                                                {t('microsoft.connect_microsoft')}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}

                            {status === 'success_paid' && (
                                <>
                                    <h2 className="ms-purchase__heading ms-purchase__success-heading">{t('microsoft.activation_success')}</h2>
                                    <div className="ms-purchase__credits-box">
                                        <div className="ms-purchase__credits-value">{creditsAdded}</div>
                                        <div className="ms-purchase__credits-label">{t('microsoft.credits_added')}</div>
                                    </div>
                                    <p className="ms-purchase__message">{t('microsoft.redirecting')}</p>
                                </>
                            )}

                            {status === 'success_free' && (
                                <>
                                    <h2 className="ms-purchase__heading ms-purchase__success-heading">{t('microsoft.activation_success')}</h2>
                                    <p className="ms-purchase__message">{message}</p>
                                    <div className="ms-purchase__actions">
                                        <button
                                            type="button"
                                            onClick={() => navigate(Routes.PRICING)}
                                            className="ms-purchase__btn ms-purchase__btn--primary"
                                        >
                                            {t('microsoft.view_plans')}
                                        </button>
                                    </div>
                                    <p className="ms-purchase__footer-note">{t('microsoft.free_starter_redirecting')}</p>
                                </>
                            )}

                            {status === 'tenant_mismatch' && (
                                <>
                                    <h2 className="ms-purchase__heading">{t('microsoft.activation_error')}</h2>
                                    <p className="ms-purchase__message ms-purchase__message--error">{message}</p>
                                    <div className="ms-purchase__actions">
                                        <button
                                            type="button"
                                            onClick={() => void handleMicrosoftSso()}
                                            className="ms-purchase__btn ms-purchase__btn--secondary"
                                        >
                                            <MicrosoftButtonIcon />
                                            {t('microsoft.reconnect_microsoft')}
                                        </button>
                                    </div>
                                </>
                            )}

                            {status === 'error' && (
                                <>
                                    <h2 className="ms-purchase__heading">{t('microsoft.activation_error')}</h2>
                                    <p className="ms-purchase__message ms-purchase__message--error">{message}</p>
                                    <div className="ms-purchase__actions ms-purchase__actions--row">
                                        <button
                                            type="button"
                                            onClick={handleRetry}
                                            className="ms-purchase__btn ms-purchase__btn--primary"
                                        >
                                            {t('microsoft.retry_activation')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => navigate('/contact')}
                                            className="ms-purchase__btn ms-purchase__btn--secondary"
                                        >
                                            {t('microsoft.contact_support')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {isSuccess && (
                        <p className="ms-purchase__footer-note">{t('microsoft.auto_redirect')}</p>
                    )}
                </div>
            </main>
        </div>
    );
};

MicrosoftPurchaseLanding.displayName = 'MicrosoftPurchaseLanding';

export default MicrosoftPurchaseLanding;
